import {
  Button,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  SxProps,
  Theme,
} from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";

export type INFormConfirmationDialogType =
  | "cancel-with-changes"
  | "publish-confirmation"
  | "delete-confirmation"
  | "delete-document-confirmation"
  | "navigate-with-changes";
export type INFormConfirmationDialogProps = {
  type?: INFormConfirmationDialogType;
};
export type INFormConfirmationDialogEvent = "confirm" | "abort";

const FORM_CONFIRMATION_DEFAULT_ACTIONS = {
  close: {
    text: "Close",
    variant: "contained",
  },
  continue: {
    text: "Continue Editing",
    variant: "text",
  },
};

const FORM_CONFIRMATION_DIALOG_TEXTS = {
  "cancel-with-changes": {
    title: "Close without saving?",
    content:
      "Closing the dialog without saving will cause all unsaved data to be lost.",
    actions: FORM_CONFIRMATION_DEFAULT_ACTIONS,
  },
  "publish-confirmation": {
    title: "Publish?",
    content: [
      {
        sx: { fontWeight: "bold" },
        text: "Notice:",
      },
      {
        text: "Finalizing this initiative will lock your current year plan figures and you will not be able to revert to draft.  You will still have the ability to delete the initiative and edit other metrics. Are you ready to publish?",
      },
    ],
    actions: {
      ...FORM_CONFIRMATION_DEFAULT_ACTIONS,
      close: {
        text: "Publish",
        variant: "contained",
      },
      continue: {
        ...FORM_CONFIRMATION_DEFAULT_ACTIONS.continue,
        text: "Abort",
      },
    },
  },
  "delete-confirmation": {
    title: "Delete?",
    content:
      "Are you sure you want to delete this initiative? This can not be undone!",
    actions: {
      continue: {
        ...FORM_CONFIRMATION_DEFAULT_ACTIONS.continue,
        text: "Abort",
      },
      close: {
        ...FORM_CONFIRMATION_DEFAULT_ACTIONS.close,
        text: "Delete",
      },
    },
  },
  "delete-document-confirmation": {
    title: "Delete?",
    content:
      "Are you sure you want to delete this Document? This can not be undone!",
    actions: {
      continue: {
        ...FORM_CONFIRMATION_DEFAULT_ACTIONS.continue,
        text: "Abort",
      },
      close: {
        ...FORM_CONFIRMATION_DEFAULT_ACTIONS.close,
        text: "Delete",
      },
    },
  },
  "navigate-with-changes": {
    title: "Continue without saving?",
    content:
      "Navigating to a different dialog without saving will cause all unsaved data to be lost.",
    actions: {
      ...FORM_CONFIRMATION_DEFAULT_ACTIONS,
      close: {
        ...FORM_CONFIRMATION_DEFAULT_ACTIONS.close,
        text: "Continue",
      },
    },
  },
} as Record<
  INFormConfirmationDialogType,
  {
    title: string;
    content:
      | string
      | {
          sx?: SxProps<Theme>;
          text: string;
        }[];
    actions?: Partial<typeof FORM_CONFIRMATION_DEFAULT_ACTIONS>;
  }
>;

export const INFormConfirmationDialog: PimoReactComponent<
  INFormConfirmationDialogProps,
  INFormConfirmationDialogEvent
> = ({ type, fireEvent }) => {
  const onClose = () => {
    fireEvent?.("confirm");
  };
  const onContinue = () => {
    fireEvent?.("abort");
  };

  if (!type) {
    return null;
  }

  const texts = FORM_CONFIRMATION_DIALOG_TEXTS[type];
  const { actions, content, title } = texts;

  return (
    <Dialog
      open
      onClose={onContinue}
      sx={{ margin: 8 }}
      PaperProps={{ sx: { borderRadius: 5 } }}
    >
      <DialogTitle>{title}</DialogTitle>
      <Divider />
      <DialogContent>
        {Array.isArray(content)
          ? content.map((item) => (
              <DialogContentText sx={item.sx} key={item.text}>
                {item.text}
              </DialogContentText>
            ))
          : content}
      </DialogContent>
      <Divider />
      <DialogActions
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
          m: 2,
          gap: 2,
        }}
      >
        {actions?.continue && (
          <Button
            type="button"
            variant={actions.continue.variant as ButtonProps["variant"]}
            onClick={onContinue}
          >
            {actions.continue.text}
          </Button>
        )}
        {actions?.close && (
          <Button
            type="button"
            variant={actions.close.variant as ButtonProps["variant"]}
            onClick={onClose}
            sx={{
              color: "#fff",
            }}
          >
            {actions.close.text}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
